import React, { useState, useEffect, useMemo } from 'react';
import { getPendingApprovals, approveDocument, bulkApproveDocuments, rejectDocument, getWorkplaceDocument } from '../services/api';
import WorkplaceNameDisplay from './WorkplaceNameDisplay';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import { io } from 'socket.io-client';
import './PendingApprovalsModal.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5005');

const docTypeLabels: Record<string, string> = {
  'riskAnalysis': 'Risk Analizi',
  'emergencyPlan': 'Acil Durum Planı',
  'trainingPlan': 'Eğitim Planı',
  'workPlan': 'Çalışma Planı',
  'instructions': 'Talimatlar',
  'ek2': 'EK-2',
  'trainings': 'Eğitim Kayıtları',
  'employeeRepresentative': 'Çalışan Temsilcisi',
  'annualEvaluation': 'Değerlendirme',
  'kkdDelivery': 'KKD Teslim',
  'detectionSuggestionBook': 'Tespit Defteri',
  'emergencyDrill': 'Tatbikat',
  'firstAid': 'İlkyardım',
  'machinePeriodicCheck': 'Makine Kontrol',
  'electricalInstallationCheck': 'Elektrik Kontrol',
  'jobTraining': 'Kurul Tutanağı',
  'test': 'TEST'
};

const getDocIcon = (type: string) => {
  const icons: Record<string, string> = {
    'riskAnalysis': 'fa-shield-alt',
    'emergencyPlan': 'fa-exclamation-triangle',
    'trainingPlan': 'fa-chalkboard-teacher',
    'workPlan': 'fa-calendar',
    'trainings': 'fa-graduation-cap',
    'ek2': 'fa-file-signature',
    'firstAid': 'fa-medkit',
    'emergencyDrill': 'fa-fire-extinguisher',
    'jobTraining': 'fa-users',
    'test': 'fa-flask'
  };
  return icons[type] || 'fa-file-pdf';
};

interface PendingDocument {
  id: number;
  workplaceId: number;
  documentType: string;
  originalFileName: string;
  fileName: string;
  uploadedDate: string;
  fileSize: number;
  mimeType: string;
  uploaderType: string;
  uploadedBy?: number;
  uploaderName?: string;
  approvalStatus?: string;
  coordinatorApprovedBy?: number;
  coordinatorApprovedAt?: string;
  coordinatorName?: string;
  createdAt: string;
  Workplace?: {
    id: number;
    name: string;
    sskRegistrationNo?: string;
    branchCity?: string;
    riskLevel?: string;
    assignedExpertId?: number;
    assignedDoctorId?: number;
    trackingExpertId?: number;
    trackingDoctorId?: number;
    Expert?: { id: number; firstName: string; lastName: string };
    Doctor?: { id: number; firstName: string; lastName: string };
    TrackingExpert?: { id: number; firstName: string; lastName: string };
    TrackingDoctor?: { id: number; firstName: string; lastName: string };
  };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onPendingCountChange?: (count: number) => void;
}

export default function PendingApprovalsModal({ isOpen, onClose, onPendingCountChange }: Props) {
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const isAuditor = user?.role === 'auditor';
  const isTwoStageCoordinator = user?.role === 'coordinator' && (user?.organizationId === 57 || Boolean(user?.Organization?.name && /lara/i.test(user.Organization.name)));

  const [documents, setDocuments] = useState<PendingDocument[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [selectedWorkplaceId, setSelectedWorkplaceId] = useState<number | null>(null);
  
  // Rejection modal state
  const [rejectingDoc, setRejectingDoc] = useState<PendingDocument | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');

  const fetchPending = async () => {
    try {
      setLoading(true);
      const data = await getPendingApprovals();
      const list = Array.isArray(data) ? data : [];
      setDocuments(list);
      if (onPendingCountChange) onPendingCountChange(list.length);
    } catch (err) {
      console.error('Error fetching pending approvals:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchPending();
    } else {
      setSelectedWorkplaceId(null);
    }
  }, [isOpen, selectedBranch]);

  // Real-time socket updates
  useEffect(() => {
    const socket = io(API_BASE_URL);
    socket.on('documentUploaded', () => fetchPending());
    socket.on('documentApproved', () => fetchPending());
    socket.on('documentRejected', () => fetchPending());
    return () => { socket.disconnect(); };
  }, [selectedBranch]);

  const handlePreview = async (doc: PendingDocument) => {
    try {
      const url = await getWorkplaceDocument(doc.workplaceId, doc.id);
      window.open(url, '_blank');
    } catch (err: any) {
      alert('Belge açılamadı: ' + (err.message || 'Bilinmeyen hata'));
    }
  };

  const handleApprove = async (doc: PendingDocument) => {
    const docName = doc.originalFileName || doc.fileName || 'Belge';
    let confirmMsg = `"${docName}" belgesini onaylayıp işletmeye işlemek istiyor musunuz?`;
    if (isAuditor) {
      confirmMsg = `"${docName}" belgesine SON ONAY verip işletmeye işlemek istiyor musunuz?`;
    } else if (isTwoStageCoordinator) {
      confirmMsg = `"${docName}" belgesini onaylayıp DENETÇİ son onayına iletmek istiyor musunuz?`;
    }

    if (!window.confirm(confirmMsg)) {
      return;
    }
    try {
      setProcessingId(doc.id);
      await approveDocument(doc.id);
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      if (onPendingCountChange) onPendingCountChange(documents.length - 1);
      window.dispatchEvent(new Event('workplaceUpdated'));
    } catch (err: any) {
      alert('Onaylama işlemi başarısız: ' + (err.message || 'Hata'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleApproveAllForWorkplace = async (workplaceId: number, wpName: string, docs: PendingDocument[]) => {
    let docsToApprove = docs;
    let isCapped = false;

    if (docs.length > 100) {
      isCapped = true;
      const confirmCapped = window.confirm(
        `Güvenlik gereği aynı anda en fazla 100 adet belge onaylanabilir.\n\n` +
        `"${wpName}" işletmesine ait toplam ${docs.length} adet bekleyen belge bulunmaktadır.\n\n` +
        `İlk 100 belgeyi şimdi onaylamak istiyor musunuz?\n(Kalan ${docs.length - 100} adet belge listede kalacaktır.)`
      );
      if (!confirmCapped) {
        return;
      }
      docsToApprove = docs.slice(0, 100);
    } else {
      let confirmMsg = `"${wpName}" işletmesine ait bekleyen ${docs.length} belgenin TÜMÜNÜ tek seferde onaylamak istiyor musunuz?`;
      if (isAuditor) {
        confirmMsg = `"${wpName}" işletmesine ait ${docs.length} belgenin TÜMÜNE tek seferde SON ONAY verip işletmeye işlemek istiyor musunuz?`;
      } else if (isTwoStageCoordinator) {
        confirmMsg = `"${wpName}" işletmesine ait ${docs.length} belgenin TÜMÜNÜ onaylayıp DENETÇİ onayına iletmek istiyor musunuz?`;
      }

      if (!window.confirm(confirmMsg)) {
        return;
      }
    }

    try {
      setLoading(true);
      const docIds = docsToApprove.map(d => d.id);
      await bulkApproveDocuments(docIds);

      const approvedIdSet = new Set(docIds);
      setDocuments(prev => prev.filter(d => !approvedIdSet.has(d.id)));
      if (onPendingCountChange) onPendingCountChange(documents.length - docIds.length);
      window.dispatchEvent(new Event('workplaceUpdated'));

      if (!isCapped && selectedWorkplaceId === workplaceId) {
        setSelectedWorkplaceId(null);
      }
    } catch (err: any) {
      alert('Toplu onaylama işlemi sırasında hata: ' + (err.message || 'Hata'));
    } finally {
      setLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectingDoc) return;
    try {
      setProcessingId(rejectingDoc.id);
      await rejectDocument(rejectingDoc.id, rejectReason);
      setDocuments(prev => prev.filter(d => d.id !== rejectingDoc.id));
      if (onPendingCountChange) onPendingCountChange(documents.length - 1);
      setRejectingDoc(null);
      setRejectReason('');
      window.dispatchEvent(new Event('workplaceUpdated'));
    } catch (err: any) {
      alert('Reddetme işlemi başarısız: ' + (err.message || 'Hata'));
    } finally {
      setProcessingId(null);
    }
  };

  const filteredDocs = useMemo(() => {
    return documents.filter(doc => {
      if (!searchTerm.trim()) return true;
      const q = searchTerm.toLocaleLowerCase('tr-TR').trim();
      const wpName = (doc.Workplace?.name || '').toLocaleLowerCase('tr-TR');
      const ssk = String(doc.Workplace?.sskRegistrationNo || '');
      const docLabel = (docTypeLabels[doc.documentType] || doc.documentType || '').toLocaleLowerCase('tr-TR');
      const fileName = (doc.originalFileName || '').toLocaleLowerCase('tr-TR');
      const uploader = (doc.uploaderName || '').toLocaleLowerCase('tr-TR');
      return wpName.includes(q) || ssk.includes(q) || docLabel.includes(q) || fileName.includes(q) || uploader.includes(q);
    });
  }, [documents, searchTerm]);

  // Group by Workplace
  const workplaceGroups = useMemo(() => {
    const groups: { [key: number]: { workplace: any; docs: PendingDocument[] } } = {};
    filteredDocs.forEach(doc => {
      const wpId = doc.workplaceId || 0;
      if (!groups[wpId]) {
        groups[wpId] = {
          workplace: doc.Workplace || { id: wpId, name: 'Bilinmeyen İşletme' },
          docs: []
        };
      }
      groups[wpId].docs.push(doc);
    });
    return Object.values(groups);
  }, [filteredDocs]);

  const selectedGroup = useMemo(() => {
    if (!selectedWorkplaceId) return null;
    return workplaceGroups.find(g => g.workplace.id === selectedWorkplaceId) || null;
  }, [workplaceGroups, selectedWorkplaceId]);

  if (!isOpen) return null;

  const formatRiskLevel = (risk?: string) => {
    if (!risk) return 'Belirtilmedi';
    const r = risk.trim().toLowerCase();
    if (r === 'low' || r === 'az tehlikeli') return 'Az Tehlikeli';
    if (r === 'medium' || r === 'tehlikeli') return 'Tehlikeli';
    if (r === 'high' || r === 'çok tehlikeli' || r === 'cok tehlikeli') return 'Çok Tehlikeli';
    return risk;
  };

  const getRiskBadgeColor = (risk?: string) => {
    if (!risk) return { bg: '#e2e8f0', color: '#475569' };
    const r = risk.toLowerCase();
    if (r === 'high' || r.includes('çok') || r.includes('cok')) return { bg: '#fee2e2', color: '#b91c1c' };
    if (r === 'medium' || r.includes('tehlikeli')) return { bg: '#fef3c7', color: '#b45309' };
    return { bg: '#dcfce7', color: '#15803d' };
  };

  return (
    <div className="pending-approvals-overlay" onClick={onClose}>
      <div className="pending-approvals-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="pending-approvals-header">
          <div className="pending-approvals-header-title">
            <h2>
              <i className={isAuditor ? "fas fa-stamp" : "fas fa-clipboard-check"} style={{ color: isAuditor ? '#fb923c' : '#f59e0b' }}></i>
              {isAuditor ? 'Denetçi Son Onay & Kalite Merkezi' : 'Ön Denetim & Belge Onay Merkezi'}
            </h2>
            <span className="pending-count-badge" style={isAuditor ? { backgroundColor: '#ea580c' } : undefined}>
              {documents.length} {isAuditor ? 'Evrak Denetim Bekliyor' : 'Belge Onay Bekliyor'}
            </span>
          </div>
          <button className="pending-approvals-close" onClick={onClose} title="Kapat">
            &times;
          </button>
        </div>

        {/* ========================================================================= */}
        {/* VIEW 2: DEDICATED WORKPLACE DOCUMENTS LIST (Tek İşyerinin Tüm Belgeleri) */}
        {/* ========================================================================= */}
        {selectedGroup ? (
          <div className="pending-detail-view">
            {/* Top Navigation / Breadcrumb Bar */}
            <div className="pending-detail-top-bar">
              <button
                type="button"
                className="btn-back-to-workplaces"
                onClick={() => setSelectedWorkplaceId(null)}
              >
                <i className="fas fa-arrow-left"></i> İşletme Listesine Dön
              </button>

              <div className="pending-detail-top-actions">
                <span className="pending-wp-doc-count-badge">
                  ⏳ {selectedGroup.docs.length} Belge Bekliyor
                </span>
                {selectedGroup.docs.length > 1 && (
                  <button
                    type="button"
                    className="btn-pending-action btn-pending-approve"
                    style={{
                      backgroundColor: isAuditor ? '#059669' : '#d97706',
                      borderColor: isAuditor ? '#059669' : '#d97706',
                      color: '#ffffff',
                      fontWeight: 'bold',
                      padding: '5px 12px',
                      borderRadius: '6px'
                    }}
                    onClick={() => handleApproveAllForWorkplace(selectedGroup.workplace.id, selectedGroup.workplace.name, selectedGroup.docs)}
                    disabled={loading}
                    title={isAuditor ? "Bu işletmedeki tüm belgelere tek seferde son onay ver" : "Tümünü onayla"}
                  >
                    <i className={isAuditor ? "fas fa-stamp" : "fas fa-check-double"}></i> {isAuditor ? 'Tümüne Son Onay Ver' : (isTwoStageCoordinator ? 'Tümünü Denetçiye İlet' : 'Tümünü Onayla')}
                  </button>
                )}
              </div>
            </div>

            {/* Workplace Info Banner */}
            <div className="pending-detail-wp-card">
              <div className="pending-detail-wp-main">
                <div className="pending-wp-title-row">
                  <span className="pending-detail-wp-title">
                    🏢 <WorkplaceNameDisplay name={selectedGroup.workplace.name} />
                  </span>
                  {selectedGroup.workplace.branchCity && (
                    <span className="pending-wp-branch">📍 {selectedGroup.workplace.branchCity}</span>
                  )}
                  {selectedGroup.workplace.riskLevel && (
                    <span
                      className="pending-wp-risk"
                      style={{
                        backgroundColor: getRiskBadgeColor(selectedGroup.workplace.riskLevel).bg,
                        color: getRiskBadgeColor(selectedGroup.workplace.riskLevel).color
                      }}
                    >
                      ⚠️ {formatRiskLevel(selectedGroup.workplace.riskLevel)}
                    </span>
                  )}
                  {selectedGroup.workplace.sskRegistrationNo && (
                    <span className="pending-wp-sicil">SGK Sicil: {selectedGroup.workplace.sskRegistrationNo}</span>
                  )}
                </div>

                <div className="pending-wp-personnel-row" style={{ marginTop: '8px' }}>
                  {selectedGroup.workplace.Expert && (
                    <span className="personnel-tag">
                      👨‍💼 <strong>Uzman:</strong> {selectedGroup.workplace.Expert.firstName} {selectedGroup.workplace.Expert.lastName}
                    </span>
                  )}
                  {selectedGroup.workplace.Doctor && (
                    <span className="personnel-tag">
                      🩺 <strong>Hekim:</strong> {selectedGroup.workplace.Doctor.firstName} {selectedGroup.workplace.Doctor.lastName}
                    </span>
                  )}
                  {selectedGroup.workplace.TrackingExpert && (
                    <span className="personnel-tag tracking">
                      🎯 <strong>Takip Uzmanı:</strong> {selectedGroup.workplace.TrackingExpert.firstName} {selectedGroup.workplace.TrackingExpert.lastName}
                    </span>
                  )}
                  {selectedGroup.workplace.TrackingDoctor && (
                    <span className="personnel-tag tracking">
                      🎯 <strong>Takip Hekimi:</strong> {selectedGroup.workplace.TrackingDoctor.firstName} {selectedGroup.workplace.TrackingDoctor.lastName}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Document Rows (Dedicated Full-Page View) */}
            <div className="pending-detail-docs-list">
              <div className="pending-detail-list-header">
                <h3>Bu İşletmeye Ait Onay Bekleyen Evraklar ({selectedGroup.docs.length})</h3>
              </div>

              {selectedGroup.docs.map((doc, idx) => (
                <div key={doc.id} className="pending-detail-doc-card">
                  <div className="pending-detail-doc-left">
                    <span className="pending-doc-index">{idx + 1}</span>
                    <span className="pending-doc-type-badge">
                      <i className={`fas ${getDocIcon(doc.documentType)}`}></i>
                      {docTypeLabels[doc.documentType] || doc.documentType}
                    </span>
                    <div className="pending-detail-file-info">
                      <div className="pending-detail-file-name" title={doc.originalFileName || doc.fileName}>
                        📄 {doc.originalFileName || doc.fileName}
                      </div>
                      <div className="pending-detail-file-meta">
                        <span><i className="far fa-calendar-alt"></i> Yükleme: {doc.uploadedDate || (doc.createdAt ? doc.createdAt.split('T')[0] : '')}</span>
                        <span>•</span>
                        <span className="pending-detail-uploader-tag">
                          <i className="fas fa-user-circle"></i> Yükleyen: <strong>{doc.uploaderName || (doc.uploaderType === 'doctor' ? 'İşyeri Hekimi' : 'Saha Uzmanı')}</strong>
                        </span>
                      </div>
                      {(doc.coordinatorName || doc.approvalStatus === 'pending_auditor') && (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          backgroundColor: '#ecfdf5',
                          color: '#065f46',
                          border: '1px solid #a7f3d0',
                          borderRadius: '6px',
                          padding: '3px 8px',
                          fontSize: '11px',
                          fontWeight: '600',
                          marginTop: '5px'
                        }}>
                          <i className="fas fa-check-circle" style={{ color: '#059669', fontSize: '13px' }}></i>
                          <span>KOORDİNATÖR ONAYI: <strong style={{ color: '#047857' }}>VERİLDİ</strong></span>
                          {doc.coordinatorName && <span>({doc.coordinatorName})</span>}
                          {doc.coordinatorApprovedAt && (
                            <span style={{ color: '#059669', fontWeight: 'normal' }}>
                              • {new Date(doc.coordinatorApprovedAt).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pending-detail-doc-actions">
                    <button
                      type="button"
                      className="btn-pending-action btn-pending-preview"
                      onClick={() => handlePreview(doc)}
                      title="Evrakı aç ve incele"
                    >
                      <i className="fas fa-eye"></i> İncele
                    </button>
                    <button
                      type="button"
                      className="btn-pending-action btn-pending-approve"
                      onClick={() => handleApprove(doc)}
                      disabled={processingId === doc.id}
                      style={isAuditor ? { backgroundColor: '#059669', borderColor: '#059669', color: '#ffffff', fontWeight: 'bold' } : undefined}
                      title={isAuditor ? "Belgeye son denetim onayını ver ve işletmeye işle" : (isTwoStageCoordinator ? "Koordinatör onayını ver ve denetçiye ilet" : "Evrakı onayla ve işletmeye işle")}
                    >
                      <i className={isAuditor ? "fas fa-stamp" : "fas fa-check"}></i> {isAuditor ? 'Son Onayı Ver' : (isTwoStageCoordinator ? 'Onayla & Denetçiye İlet' : 'Onayla')}
                    </button>
                    <button
                      type="button"
                      className="btn-pending-action btn-pending-reject"
                      onClick={() => {
                        setRejectingDoc(doc);
                        setRejectReason('');
                      }}
                      disabled={processingId === doc.id}
                      title={isAuditor ? "Belgeyi denetimden geçirme, gerekçeyle reddet" : "Evrakı reddet"}
                    >
                      <i className="fas fa-times"></i> Reddet
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* ========================================================================= */
          /* VIEW 1: MASTER LIST - TEK SATIR HALİNDE İŞLETME LİSTESİ                   */
          /* ========================================================================= */
          <>
            {/* Filter Bar */}
            <div className="pending-approvals-filter-bar">
              <i className="fas fa-search" style={{ color: '#94a3b8' }}></i>
              <input
                type="text"
                className="pending-search-input"
                placeholder="İşletme adı, SGK sicil veya uzman/hekim ile filtreleyin..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Body */}
            <div className="pending-approvals-body">
              {loading ? (
                <div className="pending-empty-state">
                  <i className="fas fa-spinner fa-spin pending-empty-icon" style={{ color: '#3b82f6' }}></i>
                  <div className="pending-empty-title">Onay bekleyen işletmeler yükleniyor...</div>
                </div>
              ) : workplaceGroups.length === 0 ? (
                <div className="pending-empty-state">
                  <i className="fas fa-check-circle pending-empty-icon"></i>
                  <div className="pending-empty-title">Onay Bekleyen Belge Bulunmuyor</div>
                  <p>Tüm saha evrakları denetlenmiş ve sisteme işlenmiştir.</p>
                </div>
              ) : (
                workplaceGroups.map(group => {
                  const wp = group.workplace;
                  const riskStyle = getRiskBadgeColor(wp.riskLevel);
                  const assignedExpertName = wp.Expert ? `${wp.Expert.firstName} ${wp.Expert.lastName}` : null;
                  const assignedDoctorName = wp.Doctor ? `${wp.Doctor.firstName} ${wp.Doctor.lastName}` : null;
                  const trackingExpertName = wp.TrackingExpert ? `${wp.TrackingExpert.firstName} ${wp.TrackingExpert.lastName}` : null;
                  const trackingDoctorName = wp.TrackingDoctor ? `${wp.TrackingDoctor.firstName} ${wp.TrackingDoctor.lastName}` : null;

                  return (
                    <div
                      key={wp.id}
                      className="pending-master-wp-row"
                      onClick={() => setSelectedWorkplaceId(wp.id)}
                    >
                      {/* Left: Workplace details */}
                      <div className="pending-master-wp-left">
                        <div className="pending-master-wp-title-row">
                          <span className="pending-master-wp-name">
                            🏢 <WorkplaceNameDisplay name={wp.name} />
                          </span>
                          {wp.branchCity && (
                            <span className="pending-wp-branch">📍 {wp.branchCity}</span>
                          )}
                          {wp.riskLevel && (
                            <span className="pending-wp-risk" style={{ backgroundColor: riskStyle.bg, color: riskStyle.color }}>
                              ⚠️ {formatRiskLevel(wp.riskLevel)}
                            </span>
                          )}
                          {wp.sskRegistrationNo && (
                            <span className="pending-wp-sicil">SGK Sicil: {wp.sskRegistrationNo}</span>
                          )}
                        </div>

                        <div className="pending-master-wp-personnel">
                          {assignedExpertName && (
                            <span className="personnel-tag">
                              👨‍💼 <strong>Uzman:</strong> {assignedExpertName}
                            </span>
                          )}
                          {assignedDoctorName && (
                            <span className="personnel-tag">
                              🩺 <strong>Hekim:</strong> {assignedDoctorName}
                            </span>
                          )}
                          {trackingExpertName && trackingExpertName !== assignedExpertName && (
                            <span className="personnel-tag tracking">
                              🎯 <strong>Takip Uzmanı:</strong> {trackingExpertName}
                            </span>
                          )}
                          {trackingDoctorName && trackingDoctorName !== assignedDoctorName && (
                            <span className="personnel-tag tracking">
                              🎯 <strong>Takip Hekimi:</strong> {trackingDoctorName}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right: Actions */}
                      <div className="pending-master-wp-right" onClick={e => e.stopPropagation()}>
                        <span className="pending-wp-doc-count-badge">
                          ⏳ {group.docs.length} Belge
                        </span>
                        
                        <button
                          type="button"
                          className="btn-open-wp-docs"
                          onClick={() => setSelectedWorkplaceId(wp.id)}
                        >
                          Belgeleri İncele ({group.docs.length}) <i className="fas fa-chevron-right"></i>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      {/* Rejection Modal */}
      {rejectingDoc && (
        <div className="rejection-modal-overlay" onClick={() => setRejectingDoc(null)}>
          <div className="rejection-modal-content" onClick={e => e.stopPropagation()}>
            <div className="rejection-modal-title">
              <i className="fas fa-exclamation-triangle"></i>
              Belgeyi Reddet
            </div>
            <p style={{ fontSize: '0.85rem', color: '#475569', margin: '0 0 10px 0' }}>
              <strong>{rejectingDoc.Workplace?.name}</strong> işletmesine ait <strong>{docTypeLabels[rejectingDoc.documentType] || rejectingDoc.documentType}</strong> belgesini reddetmek üzeresiniz.
            </p>
            <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '8px' }}>
              <strong>Yükleyen:</strong> {rejectingDoc.uploaderName || (rejectingDoc.uploaderType === 'doctor' ? 'İşyeri Hekimi' : 'Saha Uzmanı')}
            </div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
              Red Gerekçesi (Uzman/Hekim bu gerekçeyi görecektir):
            </label>
            <textarea
              className="rejection-textarea"
              placeholder="Örn: Kaşe/imza eksik, tarih uyuşmuyor veya yanlış şablon yüklenmiş..."
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              autoFocus
            />
            <div className="rejection-modal-actions">
              <button
                type="button"
                className="btn-pending-action btn-pending-preview"
                onClick={() => setRejectingDoc(null)}
              >
                Vazgeç
              </button>
              <button
                type="button"
                className="btn-pending-action btn-pending-reject"
                onClick={handleRejectSubmit}
                disabled={processingId === rejectingDoc.id}
              >
                <i className="fas fa-times-circle"></i> Reddet ve Bildir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
